
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuthState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import Footer from "@/components/footer";
import { Facebook, CheckCircle, Shield, Clock } from 'lucide-react';

export default function FacebookLoginPage() {
  const [, navigate] = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { signInWithFacebook } = useAuth();
  const { toast } = useToast();

  // Fetch configured competition redirect
  const { data: redirectConfig } = useQuery<{ competitionId: number | null; enabled: boolean }>({
    queryKey: ["/api/facebook-login-redirect"],
  });

  // Store competition ID in sessionStorage when page loads
  useEffect(() => {
    // Always clear any previous facebook competition redirect to avoid stale redirects
    sessionStorage.removeItem('facebook_oauth_comp');

    if (redirectConfig?.enabled && redirectConfig.competitionId) {
      console.log('[FACEBOOK_LOGIN] Storing competition ID for redirect:', redirectConfig.competitionId);
      sessionStorage.setItem('facebook_oauth_comp', redirectConfig.competitionId.toString());
    }

    const isMobileLogin = window.location.pathname.startsWith('/m');
    sessionStorage.setItem('facebook_oauth_mobile', isMobileLogin ? '1' : '0');
  }, [redirectConfig]);

  const handleFacebookLogin = async () => {
    setIsLoading(true);
    setError("");

    const { error } = await signInWithFacebook();

    if (error) {
      setError(error.message);
      toast({
        title: "Facebook sign in failed",
        description: error.message,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">

      <div className="container mx-auto px-4 py-12">
        <div className="flex items-center justify-center min-h-[calc(100vh-120px)]">
          <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="text-center pb-8">
              {/* Facebook Icon */}
              <div className="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6">
                <Facebook className="h-8 w-8 text-white" />
              </div>
              
              <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                Welcome to PrizeDrop
              </CardTitle>
              {redirectConfig?.enabled && (
                <CardDescription className="text-base text-gray-600 mt-2">
                  Sign in to join our latest competition and win amazing prizes!
                </CardDescription>
              )}
            </CardHeader>

            <CardContent className="px-8 pb-8">
              <div className="space-y-6">
                {/* Facebook Login Button */}
                <Button
                  onClick={handleFacebookLogin}
                  disabled={isLoading}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium text-base"
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Connecting...</span>
                    </div>
                  ) : (
                    <>
                      <Facebook className="mr-2 h-5 w-5" />
                      Continue with Facebook
                    </>
                  )}
                </Button>

                {/* Features List */}
                <div className="space-y-4 pt-6">
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <span>Quick signup with your Facebook account</span>
                  </div>
                  
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Shield className="h-4 w-4 text-blue-600" />
                    <span>Your data is secure and encrypted</span>
                  </div>
                  
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <span>Takes less than 2 minutes to complete</span>
                  </div>
                </div>

                {/* Terms */}
                <div className="pt-4 border-t">
                  <p className="text-xs text-gray-500 text-center">
                    By continuing, you agree to our{" "}
                    <button
                      onClick={() => navigate('/terms')}
                      className="text-blue-600 hover:underline"
                    >
                      Terms of Service
                    </button>
                    {" "}and{" "}
                    <button
                      onClick={() => navigate('/privacy')}
                      className="text-blue-600 hover:underline"
                    >
                      Privacy Policy
                    </button>
                  </p>
                </div>

                
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
}
